import React, { useEffect, useRef, useState } from 'react';
import { ITimeBlockListing } from '../../../../entities-generated/models/time-block/time-block-listing';
import { IAssessmentBookingRequest } from '../../../../entities-generated/models/project/assessment-booking-request';
import { IRescheduleAssessmentRequest } from '../../../../entities-generated/models/project/reschedule-assessment-request';
import * as formService from '../../form-services/assessment-scheduler';
import { AssessmentSchedulerFormDataModel } from '../../entities/assessment-sched-fm';
import { BwjSidePanel } from '../../../../framework/components/bwj-side-panel';
import { BwjButtonType } from '../../../../framework/components/bwj-button';
import { BwjGrid, Row, Col } from '../../../../framework/components/bwj-layout';
import { Spacing } from '../../../../framework/components/bwj-layout/styles';
import { BwjTextField } from '../../../../framework/components/bwj-text-field';
import { TimeBlockList } from '../../../time-block/components/time-list';
import { Text } from '@fluentui/react';
import { BwjValidityState } from '../../../../framework/entities/bwj-validity-state';

export interface ILaunchProjectAssessmentSchedulingPanelArgs
{
  projectId: string;
  isReschedulingAssessment: boolean;
  schedulingOptions: ITimeBlockListing[];
}

interface IProjectAssessmentSchedulingPanelProps {
  launchPanelArgs: ILaunchProjectAssessmentSchedulingPanelArgs | undefined;
  onScheduleAssessment: (projectId: string, request: IAssessmentBookingRequest) => void;
  onRescheduleAssessment: (projectId: string, request: IRescheduleAssessmentRequest) => void;
}

const subscriberId = 'Panel.ProjectAssessmentScheduling';

export const ProjectAssessmentSchedulingPanel: React.FunctionComponent<IProjectAssessmentSchedulingPanelProps> = (props) => {
  const [showPanel, setShowPanel] = useState(false);
  const { launchPanelArgs: args } = props;
  const formModel = formService.useFormModel(subscriberId);
  const prevArgs = useRef<ILaunchProjectAssessmentSchedulingPanelArgs | undefined>();

  useEffect(() => {
    if(!!args && args !== prevArgs.current) {
      prevArgs.current = args;
      const dataModel = new AssessmentSchedulerFormDataModel(args.isReschedulingAssessment, args.schedulingOptions);
      formService.initializeService(dataModel);
      setShowPanel(true);
    } else {
      setShowPanel(false);
    }
  }, [args]);

  const onSubmit = () => {
    formService.validateAndUpdateModel()
    .then(result => {
      if(result.isValid) {
        if(formModel.isRescheduling) {
          const request = formService.toRescheduleRequest();
          props.onRescheduleAssessment(args!.projectId, request);
        } else {
          const request = formService.toScheduleRequest();
          props.onScheduleAssessment(args!.projectId, request);
        }
        
        setShowPanel(false);
      }
    });
  }
  const onClosePanel = () => setShowPanel(false);

  return (
    <BwjSidePanel
      showPanel={showPanel}
      title={!!args && args.isReschedulingAssessment ? 'Reschedule Assessment' : 'Schedule Assessment'}
      footerButtons={[{
        buttonStyle: BwjButtonType.PrimaryStandard,
        text: !!args && args.isReschedulingAssessment ? 'Reschedule' : 'Schedule',
        onClick: onSubmit
      }, {
        buttonStyle: BwjButtonType.DefaultStandard,
        text: 'Cancel',
        onClick: onClosePanel
      }]}
      onDismiss={onClosePanel}
    >
      <BwjGrid>
        {formModel.isRescheduling === false && <>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <BwjTextField
              id={formService.customerFirstName.id}
              required={true}
              label={formService.customerFirstName.description}
              onUpdate={formService.customerFirstName.onUpdate}
              input={formModel.customerFirstName}
            />
          </Col>
        </Row>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <BwjTextField
              id={formService.customerName.id}
              required={true}
              label={formService.customerName.description}
              onUpdate={formService.customerName.onUpdate}
              input={formModel.customerName}
            />
          </Col>
        </Row>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={3}>
            <BwjTextField
              id={formService.houseNumber.id}
              required={true}
              label={formService.houseNumber.description}
              onUpdate={formService.houseNumber.onUpdate}
              input={formModel.houseNumber}
            />
          </Col>
          <Col small={9}>
            <BwjTextField
              id={formService.streetName.id}
              required={true}
              label={formService.streetName.description}
              onUpdate={formService.streetName.onUpdate}
              input={formModel.streetName}
            />
          </Col>
        </Row>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <BwjTextField
              id={formService.city.id}
              required={true}
              label={formService.city.description}
              onUpdate={formService.city.onUpdate}
              input={formModel.city}
            />
          </Col>
        </Row>
        </>}
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <BwjTextField
              id={formService.notes.id}
              required={false}
              label={formService.notes.description}
              onUpdate={formService.notes.onUpdate}
              input={formModel.notes}
              multiline={true}
            />
          </Col>
        </Row>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <div id={formService.timeBlocks.id}>
              <Text variant='medium' style={{ fontWeight: 600, display: 'block' }}>{formService.timeBlocks.description}</Text>
              {formModel.timeBlocks.validity === BwjValidityState.Invalid &&
              <Text variant='small' style={{ color: 'red', display: 'block', marginTop: '0.125rem' }}>{formModel.timeBlocks.message}</Text>}
              <div style={{ marginTop: '0.5rem' }}>
                <TimeBlockList
                  timeBlocks={formService.timeBlocks.options}
                  onSelectionChange={formService.timeBlocks.onListUpdate}
                  clearSelectionApi={undefined}
                />
              </div>
            </div>
          </Col>
        </Row>
      </BwjGrid>
    </BwjSidePanel>
  );
};
